import { useMemo, useState } from "react";
import type { CaseReviewCollectionApi } from "../../shared/hooks/useCaseReview";
import type { ReferenceDocumentView } from "@/lib/api/generated/casedb";
import type { AssessmentReferenceContextValue } from "../context/AssessmentReferenceContext";

interface ProductContext { product_id?: number; label?: string; primary?: boolean }
interface RelationshipContext {
  product_id?: number;
  event_preferred_term?: string;
  event_lower_level_term?: string;
  event_reported_term?: string;
  event_classification?: string;
  event_seriousness?: string;
  event_intensity?: string;
  daily_med_url_by_datasheet_record_id?: Record<string, string>;
  reference_documents_by_datasheet_record_id?: Record<string, ReferenceDocumentView[]>;
}
export interface AssessmentReviewContext {
  products?: ProductContext[];
  relationships?: Record<string, RelationshipContext>;
}

/** Prepares the product-filtered relationship API; the component only renders it. */
export function useAssessmentRecordReview(api: CaseReviewCollectionApi, raw: unknown) {
  const context = (raw ?? {}) as AssessmentReviewContext;
  const products = context.products ?? [];
  const [chosen, setChosen] = useState<number | undefined>();
  const productId = chosen ?? products[0]?.product_id;
  const rows = useMemo(() => api.rows.filter((row) => productId === undefined
    || context.relationships?.[row.recordId]?.product_id === productId),
  [api.rows, context.relationships, productId]);
  const selectedId = rows.some((row) => row.recordId === api.selectedId)
    ? api.selectedId : rows[0]?.recordId ?? "";
  const spec = useMemo(() => ({ ...api.spec,
    title: (_values: unknown, _optionLabel: unknown, recordId?: string) => {
      const relationship = recordId ? context.relationships?.[recordId] : undefined;
      // An event the coder did not match has no MedDRA term; the reported term still names it.
      return relationship?.event_preferred_term ?? relationship?.event_lower_level_term
        ?? relationship?.event_reported_term;
    },
    tags: (_values: unknown, _optionLabel: unknown, recordId?: string) => {
      const relationship = recordId ? context.relationships?.[recordId] : undefined;
      return [relationship?.event_classification, relationship?.event_seriousness, relationship?.event_intensity]
        .filter((tag): tag is string => !!tag);
    },
  }), [api.spec, context.relationships]);
  // Datasheet record ids are unique across the whole case, not per relationship, so every
  // relationship's map can be merged flat: the renderer only knows the datasheet row it is in,
  // never which relationship that row belongs to.
  const reference = useMemo((): AssessmentReferenceContextValue => {
    const dailyMedUrlByDatasheetRecordId: Record<string, string> = {};
    const referenceDocumentsByDatasheetRecordId: Record<string, ReferenceDocumentView[]> = {};
    Object.values(context.relationships ?? {}).forEach((relationship) => {
      Object.assign(dailyMedUrlByDatasheetRecordId, relationship.daily_med_url_by_datasheet_record_id);
      Object.assign(referenceDocumentsByDatasheetRecordId,
        relationship.reference_documents_by_datasheet_record_id);
    });
    return { dailyMedUrlByDatasheetRecordId, referenceDocumentsByDatasheetRecordId };
  }, [context.relationships]);
  return { products, productId, selectProduct: setChosen, reference,
    api: { ...api, spec, rows, selectedId } as CaseReviewCollectionApi };
}
